import {
  IconApps,
  IconDatabase,
  IconRobotFace,
  IconMessage2,
  IconX,
  IconSearch,
  IconChevronRight
} from '@tabler/icons-react'
import { Modal, ModalBody } from 'reactstrap'
//@ts-ignore
import { Avatar, Button, InputGroup } from 'dsm-react-lib'
import _endpoints from '../../_endpoints/index'
import { useEffect, useRef, useState } from 'react'
import { useFetch } from '../../_hooks/useFetch'
import { useNavigate } from 'react-router-dom'
import { getInitials } from '../../utility/Utils'
import { useSasToken } from '../../_hooks/useSasToken'
import { useThemeManager } from '../../_hooks/useThemeManager'
import { highlightSearchTerm } from '../../views/_pages/Chatbot/utils/helpers'
import { useDataboxService } from '../../views/_pages/Teams/services/databoxService'
import { useAgentService } from '../../views/_pages/Chatbot/agent/agentService'
import { getTierAbbr, getTierColor } from '../../views/_pages/Teams/modals/AddDataProductsModal'
import Skeleton from 'react-loading-skeleton'
import { useDispatch, useSelector } from 'react-redux'
import { setSelectedAgent as setSelectedAgentAction } from '../../_redux/actions/chatbotActions'
import NewAppCard from '../../views/_pages/Teams/NewAppCard'
import AgentCards from '../../views/_pages/Teams/AgentCards'
import AssetCard from '../../views/_pages/Data-marketplace/AssetCard'
import { formatLastActivity } from '../../views/_pages/Chatbot/modals/ChatSearchModal'
import { useChatService } from '../../views/_pages/Chatbot/services/chatService'
import { staticAssetsPath } from '../../configs/constants'

interface SearchModalProps {
  isOpen: boolean
  toggle: () => void
}

const SkeletonLoader = ({ type = 'square' }: { type?: 'square' | 'circle' }) => (
  <div className="flex items-start rounded relative p-[5px] ">
    <div className="mr-3 min-w-[40px] h-[40px] ml-2">
      <Skeleton
        width={40}
        height={40}
        circle={type === 'circle'}
        borderRadius={type === 'square' ? 8 : undefined}
        baseColor="var(--skeleton-base)"
      />
    </div>
    <div className="grow">
      <Skeleton width="60%" height={14} className="mb-[6px]" baseColor="var(--skeleton-base)" />
      <Skeleton width="80%" height={12} baseColor="var(--skeleton-base)" />
    </div>
  </div>
)

const SearchModal = ({ isOpen, toggle }: SearchModalProps) => {
  const [searchQuery, setSearchQuery] = useState<string | null>(null)
  const { isDarkTheme: isDarkMode } = useThemeManager()
  const [applicationList, setApplicationList] = useState<any[]>([])
  const [allApplications, setAllApplications] = useState<any[]>([])
  const [dataProducts, setDataProducts] = useState<any[]>([])
  const [allDataProducts, setAllDataProducts] = useState<any[]>([])
  const [agents, setAgents] = useState<any[]>([])
  const [allAgents, setAllAgents] = useState<any[]>([])
  const [isFiltering, getAdvanceFilter] = useFetch()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [selectedApp, setSelectedApp] = useState<any>(null)
  const [showDetailsPanel, setShowDetailsPanel] = useState(false)
  const [selectedDataProduct, setSelectedDataProduct] = useState<any>(null)
  const [showDataProductDetailsPanel, setShowDataProductDetailsPanel] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState<any>(null)
  const [showAgentDetailsPanel, setShowAgentDetailsPanel] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string>('apps')
  const { appendToken } = useSasToken()
  const { isLoadingDataProducts, fetchAvailableDataProducts } = useDataboxService()
  const { isGettingMyAgents, getAgentsListByUser } = useAgentService()
  const [chats, setChats] = useState<any[]>([])
  const [allChats, setAllChats] = useState<any[]>([])
  const { searchThreads, isSearchingThreads, getChatHistory, isFetchingChatHistory } =
    useChatService()
  const searchThreadsRef = useRef(searchThreads)
  useEffect(() => {
    searchThreadsRef.current = searchThreads
  }, [searchThreads])
  const chatbotIcon = appendToken(`${staticAssetsPath}/images/logo/ALI_Chatbot-vb.png`)
  const agentList = useSelector((state: any) => state.agentReducer.agentsList)

  const handleClose = () => {
    setSearchQuery('')
    setApplicationList([])
    setDataProducts([])
    setAgents([])
    setChats([])
    setAllChats([])
    setShowDetailsPanel(false)
    toggle()
  }

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category)
    setShowDetailsPanel(false)
    setShowDataProductDetailsPanel(false)
    setShowAgentDetailsPanel(false)
  }

  const flattenApp = (app: any) => {
    if (!app.data) return app
    const { data, ...metadata } = app
    return { ...data, ...metadata }
  }

  useEffect(() => {
    const fetchData = async () => {
      const searchString = _endpoints.search.get.concat(_endpoints.search.newArrivalsQuery)
      const response = await getAdvanceFilter(searchString)

      const applications = (response?.applications || []).map(flattenApp)
      setAllApplications(applications)

      const flattened = [...applications].reverse().slice(0, 10).map(flattenApp)
      setApplicationList(flattened)
    }
    if (isOpen && allApplications.length === 0) {
      fetchData()
    }
  }, [isOpen])

  useEffect(() => {
    const fetchDataProducts = async () => {
      try {
        const products = await fetchAvailableDataProducts(0, 100)
        setAllDataProducts(products || [])
        setDataProducts(products || [])
      } catch (error) {
        console.error('Error fetching data products:', error)
      }
    }

    if (isOpen && selectedCategory === 'dataProducts' && allDataProducts.length === 0) {
      fetchDataProducts()
    }
  }, [isOpen, selectedCategory])

  useEffect(() => {
    const fetchChats = async () => {
      try {
        const { threads } = await getChatHistory(0, 20)
        const mappedChats = (threads || []).map((chat: any) => ({
          chatId: chat?.id,
          title: chat?.name || 'New chat',
          snippet: chat?.lastMessage?.content || '',
          lastActivity: chat?.lastModifiedAt || chat?.createdAt || '',
          agentId: chat?.agentId || ''
        }))
        setAllChats(mappedChats)
        setChats(mappedChats)
      } catch (error) {
        console.error('Error fetching chats:', error)
        setAllChats([])
        setChats([])
      }
    }

    if (isOpen && selectedCategory === 'aliChats' && allChats.length === 0) {
      fetchChats()
    }
  }, [isOpen, selectedCategory])

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await getAgentsListByUser(true)
        const agentsList = response || []
        setAllAgents(agentsList)
        setAgents(agentsList)
      } catch (error) {
        console.error('Error fetching agents:', error)
      }
    }

    if (
      isOpen &&
      (selectedCategory === 'agents' || selectedCategory === 'aliChats') &&
      allAgents.length === 0
    ) {
      fetchAgents()
    }
  }, [isOpen, selectedCategory])

  useEffect(() => {
    if (searchQuery && searchQuery.trim()) {
      if (selectedCategory === 'apps') {
        const filteredApps = allApplications?.filter((app) => {
          const searchWords = searchQuery.toLowerCase().trim().split(/\s+/)
          const appName = app?.name?.toLowerCase() || ''
          return searchWords.every((word) => appName.includes(word))
        })
        setApplicationList(filteredApps || [])
      } else if (selectedCategory === 'dataProducts') {
        const filteredProducts = allDataProducts?.filter((product) => {
          const searchWords = searchQuery.toLowerCase().trim().split(/\s+/)
          const productName = product?.name?.toLowerCase() || ''
          return searchWords.every((word) => productName.includes(word))
        })
        setDataProducts(filteredProducts || [])
      } else if (selectedCategory === 'agents') {
        const filteredAgents = allAgents?.filter((agent) => {
          const searchWords = searchQuery.toLowerCase().trim().split(/\s+/)
          const agentName = agent?.name?.toLowerCase() || ''
          return searchWords.every((word) => agentName.includes(word))
        })
        setAgents(filteredAgents || [])
      }
    } else {
      if (selectedCategory === 'apps') {
        setApplicationList([...allApplications].reverse().slice(0, 10))
      } else if (selectedCategory === 'dataProducts') {
        setDataProducts(allDataProducts)
      } else if (selectedCategory === 'agents') {
        setAgents(allAgents)
      }
    }
  }, [searchQuery, allApplications, allDataProducts, allAgents, selectedCategory])

  useEffect(() => {
    if (selectedCategory !== 'aliChats') return

    const query = searchQuery?.trim() || ''
    const timer = setTimeout(async () => {
      if (!query) {
        setChats(allChats)
        return
      }

      try {
        const response = await searchThreadsRef.current(query)
        const mappedResults = (response?.results || []).map((chat: any) => ({
          chatId: chat?.threadId,
          title: chat?.threadName || 'New chat',
          snippet: chat?.snippet || '',
          lastActivity: chat?.lastActivity || '',
          agentId: chat?.agentId || ''
        }))
        setChats(mappedResults)
      } catch (error) {
        console.error('Error searching chats:', error)
        setChats([])
      }
    }, 400)

    return () => clearTimeout(timer)
  }, [searchQuery, selectedCategory, allChats])

  useEffect(() => {
    if (!(isOpen && allApplications.length > 0)) {
      return
    }

    const focusTimer = setTimeout(() => {
      if (typeof document === 'undefined') return

      const inputElement = document.querySelector('.search-input input') as HTMLInputElement | null
      inputElement?.focus()
    }, 150)

    return () => clearTimeout(focusTimer)
  }, [isOpen, allApplications])

  const onChatSelection = (chatId: string) => {
    if (!chatId) return
    handleClose()
    navigate(`/chat/${chatId}`)
  }

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      size="lg"
      contentClassName="theme-surface overflow-hidden rounded-[8px] border theme-border-default"
      className="min-w-[1000px]">
      <div className="flex items-center border-b theme-border-default theme-surface p-2">
        <div className="flex items-center w-full">
          <InputGroup
            addon={<IconSearch size={20} color="var(--input-text)" />}
            addonPosition="start"
            addonType="merged"
            isDarkMode={isDarkMode}
            className="search-input theme-input-group [&_input]:h-[50px] [&_input]:pr-10"
            placeholder={
              selectedCategory === 'apps'
                ? 'Search apps'
                : selectedCategory === 'dataProducts'
                ? 'Search data products'
                : selectedCategory === 'agents'
                ? 'Search agents'
                : selectedCategory === 'aliChats'
                ? 'Search Ali Chats'
                : 'Search data products, apps, agents, or past chats...'
            }
            value={searchQuery}
            onChange={(e: any) => setSearchQuery(e.target.value.trimStart())}
          />
          <IconX
            size={20}
            onClick={() => setSearchQuery('')}
            className="theme-input-text -ml-9 shrink-0 z-10 transition-colors duration-300 ease-in-out hover:text-[var(--text-heading)] cursor-pointer"
            data-testid="close-button"
          />
        </div>
      </div>

      <ModalBody className="p-1 theme-surface h-[570px] !max-h-[600px] overflow-hidden">
        <div className="flex gap-2 mb-1">
          <div
            onClick={() => handleCategoryChange('apps')}
            className={`cursor-pointer flex items-center gap-[8px] px-2 py-[8px] rounded-full transition-all ${
              selectedCategory === 'apps'
                ? 'bg-[var(--color-apps-badge)] text-white'
                : 'theme-surface-muted theme-text-subheading'
            }`}>
            <IconApps size={20} />
            <span className="font-medium">Apps</span>
          </div>
          <div
            onClick={() => handleCategoryChange('dataProducts')}
            className={`cursor-pointer flex items-center gap-[8px] px-2 py-[8px] rounded-full transition-all ${
              selectedCategory === 'dataProducts'
                ? 'bg-[var(--color-data-products-badge)] text-white'
                : 'theme-surface-muted theme-text-subheading'
            }`}>
            <IconDatabase size={20} />
            <span className="font-medium">Data Products</span>
          </div>
          <div
            onClick={() => handleCategoryChange('agents')}
            className={`cursor-pointer flex items-center gap-[8px] px-2 py-[8px] rounded-full transition-all ${
              selectedCategory === 'agents'
                ? 'bg-[var(--color-agents-badge)] text-white'
                : 'theme-surface-muted theme-text-subheading'
            }`}>
            <IconRobotFace size={20} />
            <span className="font-medium">Agents</span>
          </div>
          <div
            onClick={() => handleCategoryChange('aliChats')}
            className={`cursor-pointer flex items-center gap-[8px] px-2 py-[8px] rounded-full transition-all ${
              selectedCategory === 'aliChats'
                ? 'bg-[var(--color-secondary)] text-white'
                : 'theme-surface-muted theme-text-subheading'
            }`}>
            <IconMessage2 size={20} />
            <span className="font-medium">Ali Chats</span>
          </div>
        </div>

        <hr className="border-b theme-border-default pb-1 my-0 theme-surface" />

        {selectedCategory === 'apps' && (
          <>
            {!isFiltering && applicationList && applicationList.length > 0 ? (
              <div
                className="flex h-[calc(100%-50px)] mb-1"
                onMouseLeave={() => setShowDetailsPanel(false)}>
                <div
                  className={`w-full theme-surface mb-1 transition-all duration-300 overflow-y-auto overflow-x-hidden custom-scrollbar`}>
                  <div className="mb-1 flex items-center justify-between">
                    <p className="section-label mb-0 ml-1 theme-surface pl-[4px] pr-[4px]">
                      SUGGESTED APPS
                    </p>
                    <Button
                      className="!px-[12px] py-0 text-white bg-[var(--color-primary)] mr-3"
                      label="Go to App Store"
                      color="primary"
                      onClick={() => {
                        handleClose()
                        navigate(
                          searchQuery && searchQuery.trim()
                            ? `/application-store?search=${encodeURIComponent(searchQuery)}`
                            : '/application-store'
                        )
                      }}
                    />
                  </div>

                  {applicationList.map((app: any) => (
                    <div
                      key={app.id}
                      className={`flex items-start transition-colors theme-list-card group relative cursor-pointer p-[5px] ${
                        selectedApp?.id === app.id ? 'is-active' : ''
                      }`}
                      onMouseEnter={() => {
                        setSelectedApp(app)
                        setShowDetailsPanel(true)
                      }}
                      onClick={() => {
                        handleClose()
                        navigate('/')
                        setTimeout(() => {
                          navigate(`/apps/${app.uniqueName}`)
                        }, 50)
                      }}>
                      <div className="rounded-lg flex items-center justify-center mr-3 text-white">
                        <Avatar
                          content={appendToken(app?.appIcon?.url)}
                          size="40px"
                          shape="rounded"
                        />
                      </div>
                      <div className="grow">
                        <p className="card-header-text m-0 !hover:font-bold pt-[4px]">
                          {searchQuery && searchQuery.trim()
                            ? highlightSearchTerm(app.name, searchQuery)
                            : app.name}
                        </p>
                        <p className="caption-text mb-0 line-clamp-1 text-ellipsis">
                          {app.shortDescription || ''}
                        </p>
                      </div>
                      <div className="opacity-0 group-hover:!opacity-80 transition-opacity m-auto rounded p-1">
                        <IconChevronRight
                          className="theme-text-subheading"
                          size={20}
                          onClick={() => setShowDetailsPanel(true)}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {showDetailsPanel && selectedApp && (
                  <div className="w-[620px] px-0.5 py-4 transition-all duration-300 overflow-y-auto overflow-x-hidden custom-scrollbar mb-[1rem]">
                    <NewAppCard
                      application={{
                        ...selectedApp,
                        appIconUrl: selectedApp?.appIcon?.url || selectedApp?.appIconUrl,
                        dateModified: selectedApp?.modifiedAt || selectedApp?.dateModified,
                        lastModifiedBy: selectedApp?.modifiedBy || selectedApp?.lastModifiedBy,
                        businessOwners: (
                          selectedApp?.applicationBusinessContacts ||
                          selectedApp?.businessOwners ||
                          []
                        ).map((owner: any) => ({
                          name: owner?.name || owner?.displayName,
                          date: owner?.date,
                          profilePictureBase64Encoded: owner?.profilePictureBase64Encoded
                        }))
                      }}
                      id={selectedApp.id}
                      searchTerm={searchQuery || undefined}
                      onBeforeNavigate={handleClose}
                    />
                  </div>
                )}
              </div>
            ) : !isFiltering && searchQuery ? (
              <div className="theme-text-subheading text-center py-5 ">
                <IconApps size={48} className="mb-3" />
                <p>No results found for &quot;{searchQuery}&quot;</p>
              </div>
            ) : (
              <div className="overflow-y-auto pt-[3rem] h-[calc(100%-150px)] theme-surface">
                {[...Array(6)].map((_, index) => (
                  <SkeletonLoader key={index} />
                ))}
              </div>
            )}
          </>
        )}

        {selectedCategory === 'dataProducts' && (
          <>
            {!isLoadingDataProducts && dataProducts && dataProducts.length > 0 ? (
              <div
                className="flex h-[calc(100%-50px)]"
                onMouseLeave={() => setShowDataProductDetailsPanel(false)}>
                <div
                  className={`w-full theme-surface mb-[1rem] transition-all duration-300 overflow-y-auto overflow-x-hidden custom-scrollbar pr-1`}>
                  <div className="mb-1 flex items-center justify-between">
                    <p className="section-label mb-0 ml-1 theme-surface pl-[4px] pr-[4px]">
                      SUGGESTED DATA PRODUCTS
                    </p>
                    <Button
                      className="!px-[12px] py-0 text-white bg-[var(--color-primary)] mr-3"
                      label="Open Data Marketplace"
                      color="primary"
                      onClick={() => {
                        handleClose()
                        navigate(
                          searchQuery?.trim()
                            ? `/data-marketplace?search=${encodeURIComponent(searchQuery)}`
                            : '/data-marketplace'
                        )
                      }}
                    />
                  </div>

                  {dataProducts.map((product: any) => (
                    <div
                      key={product.id}
                      className={`flex items-center transition-colors theme-list-card group relative cursor-pointer p-[5px] ml-2 ${
                        selectedDataProduct?.id === product.id ? 'is-active' : ''
                      }`}
                      onMouseEnter={() => {
                        setSelectedDataProduct(product)
                        setShowDataProductDetailsPanel(true)
                      }}
                      onClick={() => {
                        handleClose()
                        navigate(`/data-marketplace/${product.id}/details`)
                      }}>
                      <div className="rounded-lg flex items-center justify-center mr-3 min-w-[40px] h-[40px]">
                        <div className="rounded-lg flex items-center justify-center mr-1 text-white min0w-[40px] h-[40px]">
                          <div
                            className={`w-10 h-10 ${getTierColor(
                              product.tier
                            )} text-white text-sm font-bold rounded flex items-center justify-center flex-shrink-0`}>
                            {getTierAbbr(product.tier)}
                          </div>
                        </div>
                      </div>
                      <div className="grow">
                        <p className="card-header-text !mb-0 pt-[4px]">
                          {searchQuery && searchQuery.trim()
                            ? highlightSearchTerm(product.name, searchQuery)
                            : product.name}
                        </p>
                        <p className="caption-text mb-0 line-clamp-1 text-ellipsis">
                          {product.description || 'No description available'}
                        </p>
                      </div>
                      <div className="opacity-0 group-hover:!opacity-80 transition-opacity m-auto rounded p-1">
                        <IconChevronRight className="theme-text-subheading" size={20} />
                      </div>
                    </div>
                  ))}
                </div>

                {showDataProductDetailsPanel && selectedDataProduct && (
                  <div className="w-[800px] px-1 py-2 mb-1 transition-all duration-300 overflow-y-auto overflow-x-hidden custom-scrollbar">
                    <AssetCard
                      asset={{
                        id: selectedDataProduct?.id,
                        name: selectedDataProduct?.name,
                        description: selectedDataProduct?.description || 'No description available',
                        dataCategory: selectedDataProduct?.dataCategory || '',
                        collection: selectedDataProduct?.collection || '',
                        dataProviders:
                          selectedDataProduct?.dataProviders ||
                          selectedDataProduct?.owners?.join(', ') ||
                          '',
                        imageSrc: '',
                        tier: selectedDataProduct?.tier || '',
                        dataProductName: selectedDataProduct?.name,
                        securityClassification: selectedDataProduct?.securityClassification
                      }}
                      showButtons={true}
                      searchTerm={searchQuery || undefined}
                      metadataSource={selectedDataProduct?.metadataSource}
                      isReadOnlyCard={true}
                      onBeforeNavigate={handleClose}
                    />
                  </div>
                )}
              </div>
            ) : isLoadingDataProducts ? (
              <div className="overflow-y-auto pt-[3rem] h-[calc(100%-150px)] theme-surface">
                {[...Array(6)].map((_, index) => (
                  <SkeletonLoader key={index} type="square" />
                ))}
              </div>
            ) : searchQuery ? (
              <div className="theme-text-subheading text-center py-5">
                <IconDatabase size={48} className="mb-3 text-[var(--icon-muted)]" />
                <p>No data products found for &quot;{searchQuery}&quot;</p>
              </div>
            ) : (
              <div className="overflow-y-auto pt-[3rem] h-[calc(100%-150px)] theme-surface">
                {[...Array(6)].map((_, index) => (
                  <SkeletonLoader key={index} type="square" />
                ))}
              </div>
            )}
          </>
        )}

        {selectedCategory === 'agents' && (
          <>
            {!isGettingMyAgents && agents && agents.length > 0 ? (
              <div
                className="flex h-[calc(100%-50px)]"
                onMouseLeave={() => setShowAgentDetailsPanel(false)}>
                <div
                  className={`w-full theme-surface mb-[1rem] transition-all duration-300 overflow-y-auto overflow-x-hidden custom-scrollbar pr-1`}>
                  <div className="mb-1 flex items-center justify-between">
                    <p className="section-label mb-0 ml-1 theme-surface pl-[4px] pr-[4px]">
                      SUGGESTED AGENTS
                    </p>
                    <Button
                      className="!px-[12px] py-0 text-white bg-[var(--color-primary)] mr-3"
                      label="View All Agents"
                      color="primary"
                      onClick={() => {
                        handleClose()
                        navigate(
                          searchQuery?.trim()
                            ? `/manage-agents?search=${encodeURIComponent(searchQuery)}`
                            : '/manage-agents'
                        )
                      }}
                    />
                  </div>

                  {agents.map((agent: any) => (
                    <div
                      key={agent.id}
                      className={`flex items-start transition-colors theme-list-card group relative cursor-pointer p-[5px] ${
                        selectedAgent?.id === agent.id ? 'is-active' : ''
                      }`}
                      onMouseEnter={() => {
                        setSelectedAgent(agent)
                        setShowAgentDetailsPanel(true)
                      }}
                      onClick={() => {
                        dispatch(setSelectedAgentAction({ ...agent }))
                        handleClose()
                        navigate('/chat', { state: { fromAgentCard: true } })
                      }}>
                      <div className="rounded-lg flex items-center justify-center text-white min-w-[40px] h-[40px]">
                        <Avatar
                          content={
                            agent?.avatarUrl ? agent.avatarUrl : getInitials(agent?.name || 'AI')
                          }
                          size="40px"
                          className="!ml-0 !mr-[1rem] !min-w-10"
                        />
                      </div>
                      <div className="grow">
                        <p className="card-header-text m-0 pt-[4px]">
                          {searchQuery && searchQuery.trim()
                            ? highlightSearchTerm(agent.name, searchQuery)
                            : agent.name}
                        </p>
                        <p className="caption-text mb-0 line-clamp-1 text-ellipsis">
                          {agent.description || agent.systemPrompt || 'No description available'}
                        </p>
                      </div>
                      <div className="opacity-0 group-hover:!opacity-80 transition-opacity m-auto rounded p-1">
                        <IconChevronRight className="theme-text-subheading" size={20} />
                      </div>
                    </div>
                  ))}
                </div>

                {showAgentDetailsPanel && selectedAgent && (
                  <div className="w-[700px] py-3 px-1 transition-all duration-300 overflow-y-auto overflow-x-hidden custom-scrollbar">
                    <AgentCards
                      agentData={[
                        {
                          ...selectedAgent,
                          avatarUrl: selectedAgent?.avatarUrl,
                          name: selectedAgent?.name,
                          description: selectedAgent?.description || selectedAgent?.systemPrompt,
                          agentType: selectedAgent?.agentType,
                          source: selectedAgent?.source || 'Centrali',
                          skills: selectedAgent?.skills,
                          plugins: selectedAgent?.plugins
                        }
                      ]}
                      role="Member"
                      onBeforeNavigate={handleClose}
                    />
                  </div>
                )}
              </div>
            ) : isGettingMyAgents ? (
              <div className="overflow-y-auto pt-[3rem] h-[calc(100%-150px)] theme-surface">
                {[...Array(6)].map((_, index) => (
                  <SkeletonLoader key={index} type="circle" />
                ))}
              </div>
            ) : searchQuery ? (
              <div className="theme-text-subheading text-center py-5">
                <IconRobotFace size={48} className="mb-3 text-[var(--icon-muted)]" />
                <p>No agents found for &quot;{searchQuery}&quot;</p>
              </div>
            ) : (
              <div className="overflow-y-auto pt-[3rem] h-[calc(100%-150px)] theme-surface">
                {[...Array(6)].map((_, index) => (
                  <SkeletonLoader key={index} type="circle" />
                ))}
              </div>
            )}
          </>
        )}

        {selectedCategory === 'aliChats' && (
          <div className="flex h-[calc(100%-50px)]">
            <div className="w-full theme-surface mb-[1rem] transition-all duration-300 flex flex-col min-h-0">
              <div className="mb-1 flex items-center justify-between shrink-0">
                <p className="section-label mb-0 ml-1 theme-surface pl-[4px] pr-[4px]">
                  LAST ALI CHAT
                </p>
                <Button
                  className="!px-[12px] py-0 text-white bg-[var(--color-primary)] mr-3"
                  label="Go to Ali chats"
                  color="primary"
                  onClick={() => {
                    handleClose()
                    navigate('/chat')
                  }}
                />
              </div>
              {isFetchingChatHistory || isSearchingThreads || isGettingMyAgents ? (
                <div className="flex-1 min-h-0 overflow-y-auto pt-2 theme-surface custom-scrollbar">
                  {[...Array(6)].map((_, index) => (
                    <SkeletonLoader key={index} type="circle" />
                  ))}
                </div>
              ) : chats.length > 0 ? (
                <ul className="flex-1 min-h-0 m-0 p-0 list-none overflow-y-auto theme-surface custom-scrollbar">
                  {chats.map((chat: any, i: number) =>
                    chat.isGroup ? (
                      /* Group Header */
                      <li
                        key={`group-${i}`}
                        className="section-label px-[14px] py-[6px] theme-surface-muted">
                        {chat.groupName}
                      </li>
                    ) : (
                      /* Chat Item */
                      <li
                        key={chat.chatId}
                        className="flex items-center gap-1 px-[14px] py-[10px] cursor-pointer theme-list-card transition-colors duration-150"
                        onClick={() => {
                          onChatSelection(chat.chatId)
                        }}>
                        {/* Avatar */}
                        <div className="flex-shrink-0 flex items-center justify-center">
                          {chat?.agentId &&
                          agents.find((agent: any) => agent?.id === chat?.agentId) ? (
                            agents.find((agent: any) => agent?.id === chat?.agentId)?.avatarUrl ? (
                              <Avatar
                                size="34px"
                                content={
                                  agents.find((agent: any) => agent?.id === chat?.agentId)
                                    ?.avatarUrl
                                }
                                className="shrink-0 !m-0"
                              />
                            ) : (
                              <Avatar
                                size="38px"
                                content={getInitials(
                                  agents.find((agent: any) => agent?.id === chat?.agentId)?.name
                                )}
                                className="shrink-0 !m-0"
                              />
                            )
                          ) : (
                            <div className="w-10 h-10 rounded-full theme-surface border theme-border-default flex items-center justify-center">
                              <Avatar size="42px" content={chatbotIcon} className="shrink-0 !m-0" />
                            </div>
                          )}
                        </div>
                        {/* Chat Content */}
                        <div className="flex-1 min-w-0">
                          <span className="info-label truncate block">
                            {searchQuery?.trim()
                              ? highlightSearchTerm(chat.title || 'New chat', searchQuery)
                              : chat.title || 'New chat'}
                          </span>
                          {searchQuery && searchQuery.trim().length > 0 && (
                            <p className="caption-text mb-0 line-clamp-1">
                              {searchQuery?.trim()
                                ? highlightSearchTerm(chat.snippet || '', searchQuery)
                                : chat.snippet || 'No preview available'}
                            </p>
                          )}
                        </div>
                        {/* Timestamp */}
                        {chat.lastActivity && (
                          <span className="caption-text whitespace-nowrap flex-shrink-0">
                            {formatLastActivity(chat.lastActivity)}
                          </span>
                        )}
                      </li>
                    )
                  )}
                </ul>
              ) : (
                <div className="flex-1 min-h-0 theme-text-subheading text-center flex flex-col items-center px-3 -translate-y-4">
                  <IconMessage2 size={48} className="mb-2 text-[var(--icon-muted)]" />
                  <p className="mb-0 mt-0">
                    {searchQuery?.trim()
                      ? `No chats found for "${searchQuery}"`
                      : 'No chat history available'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </ModalBody>
    </Modal>
  )
}

export default SearchModal
